import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ApiBaseUrl } from "../utils/Constants";

const authHeaders = () => ({ Authorization: localStorage.getItem("token") });

export const getPatients = createAsyncThunk(
    "patients/getAll",
    async (projectId, { rejectWithValue }) => {
        try {
            const res = await axios.get(`${ApiBaseUrl}/v1/user/patients?projectId=${projectId}`, { headers: authHeaders() });
            return res.data;
        } catch (e) { return rejectWithValue(e.response?.data || { message: e.message }); }
    }
);

export const addPatient = createAsyncThunk(
    "patients/add",
    async (payload, { rejectWithValue }) => {
        try {
            const res = await axios.post(`${ApiBaseUrl}/v1/user/patient/add`, payload, { headers: authHeaders() });
            return res.data;
        } catch (e) { return rejectWithValue(e.response?.data || { message: e.message }); }
    }
);

export const updatePatient = createAsyncThunk(
    "patients/update",
    async (payload, { rejectWithValue }) => {
        try {
            const res = await axios.put(`${ApiBaseUrl}/v1/user/patient/update`, payload, { headers: authHeaders() });
            return res.data;
        } catch (e) { return rejectWithValue(e.response?.data || { message: e.message }); }
    }
);

export const deletePatient = createAsyncThunk(
    "patients/delete",
    async (patientId, { rejectWithValue }) => {
        try {
            const res = await axios.delete(`${ApiBaseUrl}/v1/user/patient/${patientId}`, { headers: authHeaders() });
            return { ...res.data, patientId };
        } catch (e) { return rejectWithValue(e.response?.data || { message: e.message }); }
    }
);

const patientSlice = createSlice({
    name: "patients",
    initialState: {
        list: [],
        selectedPatientId: null,
        isLoading: false,
        isSaving: false,
        error: null,
    },
    reducers: {
        setSelectedPatient: (state, action) => {
            state.selectedPatientId = action.payload;
        },
        clearPatients: (state) => {
            state.list = [];
            state.selectedPatientId = null;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getPatients.pending, (state) => { state.isLoading = true; state.error = null; })
            .addCase(getPatients.fulfilled, (state, action) => {
                state.isLoading = false;
                state.list = action.payload?.data || [];
                // Auto-select first patient if none selected or selected not in list
                if (state.list.length > 0) {
                    const ids = state.list.map(p => p._id);
                    if (!state.selectedPatientId || !ids.includes(state.selectedPatientId)) {
                        state.selectedPatientId = state.list[0]._id;
                    }
                } else {
                    state.selectedPatientId = null;
                }
            })
            .addCase(getPatients.rejected, (state) => { state.isLoading = false; })

            .addCase(addPatient.pending, (state) => { state.isSaving = true; state.error = null; })
            .addCase(addPatient.fulfilled, (state, action) => {
                state.isSaving = false;
                if (action.payload?.status === 1) {
                    state.list = [action.payload.data, ...state.list];
                    state.selectedPatientId = action.payload.data._id;
                } else {
                    state.error = action.payload?.message || "Failed to add patient";
                }
            })
            .addCase(addPatient.rejected, (state, action) => {
                state.isSaving = false;
                state.error = action.payload?.message || "Failed to add patient";
            })

            .addCase(deletePatient.fulfilled, (state, action) => {
                state.list = state.list.filter(p => p._id !== action.payload.patientId);
                if (state.selectedPatientId === action.payload.patientId) {
                    state.selectedPatientId = state.list[0]?._id || null;
                }
            });
    },
});

export const { setSelectedPatient, clearPatients } = patientSlice.actions;
export default patientSlice.reducer;